import { useCallback, useMemo, useState } from "react";
import { Mail } from "lucide-react";
import { useMailProviders } from "@/hooks/useMailProviders";
import { connectGmailWithGoogle } from "@/lib/accounts/gmailOAuthPopup";
import { connectMicrosoftMailbox } from "@/lib/accounts/microsoftOAuthPopup";
import { AccountConnectPicker } from "./AccountConnectPicker";
import { AccountConnectForm } from "./AccountConnectForm";
import { mailConnectIsOAuthOnlyProvider } from "./providerConnectOrder";
import { MailLoadingState } from "@/features/shared/MailLoadingState";
import type { MailAccountSummary } from "@/lib/types/mail";

type Props = {
  onConnected: (account?: MailAccountSummary) => void;
};

export function AccountConnectScreen({ onConnected }: Props) {
  const { providers, loading } = useMailProviders();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [microsoftBusy, setMicrosoftBusy] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);

  const selected = useMemo(
    () => providers.find((p) => p.id === selectedId) ?? null,
    [providers, selectedId]
  );

  const handleGoogleConnect = useCallback(async () => {
    if (googleBusy || microsoftBusy) return;

    setGoogleBusy(true);
    setOauthError(null);
    try {
      const account = await connectGmailWithGoogle();
      onConnected(account);
    } catch (err) {
      setOauthError(err instanceof Error ? err.message : "Connexion Google impossible.");
    } finally {
      setGoogleBusy(false);
    }
  }, [googleBusy, microsoftBusy, onConnected]);

  const handleMicrosoftConnect = useCallback(async () => {
    if (googleBusy || microsoftBusy) return;

    setMicrosoftBusy(true);
    setOauthError(null);
    try {
      const account = await connectMicrosoftMailbox();
      onConnected(account);
    } catch (err) {
      setOauthError(err instanceof Error ? err.message : "Connexion Microsoft impossible.");
    } finally {
      setMicrosoftBusy(false);
    }
  }, [googleBusy, microsoftBusy, onConnected]);

  return (
    <div className="flex h-full min-h-0 items-center justify-center overflow-auto p-4 md:p-6">
      <div className="mail-panel w-full max-w-3xl p-6 shadow-[var(--mail-shadow-md)] md:p-8">
        <div className="mb-6 flex items-center gap-3">
          <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[var(--mail-accent-soft)]">
            <Mail className="h-6 w-6 text-[var(--mail-accent)]" aria-hidden />
          </span>
          <h2 className="text-lg font-semibold tracking-tight text-[var(--mail-text)]">
            Connecter votre messagerie
          </h2>
        </div>

        <div className="space-y-6">
          <section>
            {loading ? (
              <MailLoadingState variant="webmail" layout="compact" className="py-6" />
            ) : (
              <AccountConnectPicker
                providers={providers}
                selectedId={selectedId}
                onSelect={setSelectedId}
                onGoogleConnect={() => void handleGoogleConnect()}
                onMicrosoftConnect={() => void handleMicrosoftConnect()}
                googleBusy={googleBusy}
                microsoftBusy={microsoftBusy}
              />
            )}
            {oauthError ? (
              <p className="mt-3 text-sm text-[var(--mail-danger-text)]" role="alert">
                {oauthError}
              </p>
            ) : null}
          </section>

          {selected && !mailConnectIsOAuthOnlyProvider(selected, providers) ? (
            <section>
              <h3 className="mb-2 px-1 text-[10px] font-semibold uppercase tracking-wider text-[var(--mail-muted)]">
                Identifiants — {selected.label}
              </h3>
              <AccountConnectForm
                provider={selected}
                onSuccess={onConnected}
                hideGoogleOAuth
                hideMicrosoftOAuth
              />
            </section>
          ) : null}
        </div>
      </div>
    </div>
  );
}
